// src/game-system/races/types.js

/**
 * Race type definitions for the skill-based character system
 */

/**
 * @typedef {Object} RaceSize
 * @property {string} category - Size category (Small, Medium, Large)
 * @property {number} carryingCapacityMultiplier - Multiplier for carrying capacity
 * @property {string[]} limitations - Any size-based limitations
 */

/**
 * @typedef {Object} AttributeBonus
 * @property {number} strength - Bonus to strength
 * @property {number} dexterity - Bonus to dexterity
 * @property {number} constitution - Bonus to constitution
 * @property {number} intelligence - Bonus to intelligence
 * @property {number} wisdom - Bonus to wisdom
 * @property {number} charisma - Bonus to charisma
 */

/**
 * @typedef {Object} SkillBonus
 * @property {string} skillId - ID of the skill
 * @property {number} value - Bonus value
 */

/**
 * @typedef {Object} RacialFeature
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {string} description - Feature description
 * @property {Function} [apply] - Function to apply feature effects
 */

/**
 * @typedef {Object} RacialLanguage
 * @property {string} id - Language identifier
 * @property {string} name - Display name
 * @property {boolean} isStarting - Whether this is known at start
 * @property {string[]} [dialects] - Optional dialects
 */

/**
 * Common race properties
 */
export const RACE_PROPERTIES = {
  DARKVISION: "darkvision",
  SUPERIOR_DARKVISION: "superiorDarkvision",
  NATURAL_ARMOR: "naturalArmor",
  DAMAGE_RESISTANCE: "damageResistance",
  SKILL_PROFICIENCY: "skillProficiency",
  TOOL_PROFICIENCY: "toolProficiency",
  WEAPON_PROFICIENCY: "weaponProficiency",
  SAVING_THROW_ADVANTAGE: "savingThrowAdvantage",
  BONUS_ACTION: "bonusAction",
  NATURAL_WEAPON: "naturalWeapon",
  SPELL_RESISTANCE: "spellResistance",
  EXTRA_LANGUAGE: "extraLanguage",
  FAVORED_TERRAIN: "favoredTerrain"
};

/**
 * Categories for racial abilities
 */
export const RACIAL_ABILITY_CATEGORIES = {
  PHYSICAL: "physical",
  MAGICAL: "magical",
  SOCIAL: "social",
  DEFENSIVE: "defensive",
  MOBILITY: "mobility",
  PERCEPTION: "perception",
  UTILITY: "utility",
  RESISTANCE: "resistance"
};

/**
 * Damage types for resistances and vulnerabilities
 */
export const DAMAGE_TYPES = {
  ACID: "acid",
  BLUDGEONING: "bludgeoning",
  COLD: "cold",
  FIRE: "fire",
  FORCE: "force",
  LIGHTNING: "lightning",
  NECROTIC: "necrotic",
  PIERCING: "piercing",
  POISON: "poison",
  PSYCHIC: "psychic",
  RADIANT: "radiant",
  SLASHING: "slashing",
  THUNDER: "thunder"
};

/**
 * Common languages in the game world
 */
export const LANGUAGES = {
  COMMON: "common",
  DWARVISH: "dwarvish",
  ELVISH: "elvish",
  GIANT: "giant",
  GNOMISH: "gnomish",
  GOBLIN: "goblin",
  HALFLING: "halfling",
  ORC: "orc",
  ABYSSAL: "abyssal",
  CELESTIAL: "celestial",
  DRACONIC: "draconic",
  DEEP_SPEECH: "deepSpeech",
  INFERNAL: "infernal",
  PRIMORDIAL: "primordial",
  SYLVAN: "sylvan",
  UNDERCOMMON: "undercommon"
};

// Export all type definitions and constants
export default {
  RACE_PROPERTIES,
  RACIAL_ABILITY_CATEGORIES,
  DAMAGE_TYPES,
  LANGUAGES
};